import React, { SyntheticEvent, useContext, useState, useEffect } from 'react';

import { FatalAttraction as FA } from '@vpr/tracking';

import { useCapability } from '@vimeo/core/hooks/use-capability';
import { translate } from '@vimeo/i18n-runtime';
import { Button, Notification as IrisNotification, Tip } from '@vimeo/iris/components';
import { Cart, VideoStack } from '@vimeo/iris/icons';
import { AccountType, User } from '@vimeo/viewer';
import { ViewerContext } from '@vimeo/viewer';

import { Pictures, StockClip } from '../../types';
import { THEME_DARK, USER_KEY } from '../../utils/constants';
import FATAL_ATTRACTION_CONFIG from '../../utils/fatalAttractionConfig';
import { idFromUri, isSmartPhoneOrTablet } from '../../utils/helpers';
import { Clip, Notification } from '../Notification';
import {
  AddButton,
  ButtonBackground,
  StockIconDark,
  StockIconLight,
  StockWrapper,
  StyledButtons,
  StyledDownloadIcon,
} from './Styles';

interface StockService {
  addToCart: (clipId: number, xsrft: string, userId: number) => Promise<Error | undefined>;
  downloadStockClip: (clip: StockClip) => Promise<Error | undefined>;
  addToAccount: (user: User, clipId: number, apiUrl: string, jwt: string) => Promise<Response>;
}

type Props = {
  clipId: number;
  pictures: Pictures;
  name: string;
  theme?: string;
  featured?: boolean;
  isFree?: boolean;
  user?: User;
  stockService?: StockService;
};

export default function ClipStockDetails({
  clipId,
  name,
  pictures,
  theme,
  featured,
  isFree,
  user,
  stockService,
}: Props) {
  const viewer = useContext(ViewerContext);
  const { capabilities, loading } = useCapability(['hasStockEnterprise', 'canReceiveFreeStock']);
  if (!viewer || loading) {
    return null;
  }

  const { jwt, xsrft, apiUrl } = viewer;
  const [errorMessage, setErrorMessage] = useState<boolean | undefined>();
  const [isAddedToAccount, setIsAddedToAccount] = useState(false);
  const [addedClip, setAddedClip] = useState<Clip | undefined>();
  const [showActionOption, setShowActionOption] = useState(false);

  useEffect(() => {
    // Dismiss notification after 4 seconds
    let notificationTimer: number | undefined;
    if (isAddedToAccount) {
      notificationTimer = setTimeout(() => {
        dismissNotification();
      }, 4000);
    }
    return () => {
      if (notificationTimer) {
        clearTimeout(notificationTimer);
      }
    };
  }, [isAddedToAccount]);

  const hasStockEnterprise = capabilities?.hasStockEnterprise;
  const canReceiveFreeStock = capabilities?.canReceiveFreeStock;
  const isPaid = user && user.account !== AccountType.Basic;

  const handleAddToCart = () => {
    if (stockService && user) {
      stockService
        .addToCart(clipId, xsrft, idFromUri(user?.uri, USER_KEY))
        .then((error?: Error) => {
          if (error && error.message) {
            const message = translate('This clip could not be added to the cart.');
            if (errorMessage !== message) {
              setErrorMessage(errorMessage);
              setIsAddedToAccount(true);
            }
          }
        });
    }
  };

  const redirectToStock = () => {
    location.href = `/stock`;
  };

  const handleAddToAccount = () => {
    if (stockService && user) {
      stockService.addToAccount(user, clipId, apiUrl, jwt).then((resp: any) => {
        if (resp && resp.message) {
          const message = translate('Something went wrong. Please try again.');
          if (errorMessage !== message) {
            setErrorMessage(message);
            setShowActionOption(false);
          }
        } else {
          notifySuccess(resp);
        }
      });
    }
  };

  const dismissNotification = () => {
    setAddedClip(undefined);
    setIsAddedToAccount(false);
  };

  const notifySuccess = (clip: Clip) => {
    setAddedClip(clip);
    setIsAddedToAccount(true);
  };

  return (
    <div>
      {isAddedToAccount && addedClip && <Notification clip={addedClip} />}

      <StockWrapper isFeatured={featured}>
        {!featured &&
          (theme === THEME_DARK ? (
            <StockIconLight onClick={redirectToStock} />
          ) : (
            <StockIconDark onClick={redirectToStock} />
          ))}
        <StyledButtons
          isPaid={isPaid}
          isFree={isFree}
          isSmartPhoneOrTablet={isSmartPhoneOrTablet()}
        >
          {!isSmartPhoneOrTablet() && (
            <Tip content={translate('Download preview')}>
              <ButtonBackground $theme={theme ?? ''}>
                <Button
                  icon={<StyledDownloadIcon />}
                  format="soft"
                  variant="minimalTransparent"
                  size="md"
                  onClick={(e: SyntheticEvent) => {
                    e.stopPropagation();
                    FA.trackClick({
                      ...FATAL_ATTRACTION_CONFIG.faSpecs.downloadStockPreview,
                      keyword: clipId,
                    });
                    if (stockService) {
                      stockService.downloadStockClip({
                        clipId,
                        name,
                        pictures,
                      });
                    }
                  }}
                />
              </ButtonBackground>
            </Tip>
          )}
          {!isFree && !hasStockEnterprise ? (
            <AddButton>
              <Tip content={translate('Add to cart')}>
                <Button
                  icon={<Cart />}
                  format="primary"
                  size="md"
                  onClick={(e: SyntheticEvent) => {
                    e.stopPropagation();
                    FA.trackClick({
                      ...FATAL_ATTRACTION_CONFIG.faSpecs.addStockToCart,
                      keyword: clipId,
                    });
                    handleAddToCart();
                  }}
                />
              </Tip>
            </AddButton>
          ) : isPaid && canReceiveFreeStock ? (
            <AddButton>
              <Tip content={translate('Add to account')}>
                <Button
                  icon={<VideoStack />}
                  format="primary"
                  size="md"
                  onClick={(e: SyntheticEvent) => {
                    e.stopPropagation();
                    FA.trackClick({
                      ...FATAL_ATTRACTION_CONFIG.faSpecs.addStockToAccount,
                      keyword: clipId,
                    });
                    handleAddToAccount();
                  }}
                />
              </Tip>
            </AddButton>
          ) : null}
        </StyledButtons>
      </StockWrapper>

      {errorMessage && (
        <IrisNotification
          showing={!!errorMessage}
          onActionClick={handleAddToCart}
          actionLabel={showActionOption ? translate('Try again.') : ''}
          onComplete={dismissNotification}
          // @ts-ignore incompatible types
          content={errorMessage}
        />
      )}
    </div>
  );
}
